<script lang="ts">
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import type { ImageWithMeta } from "$lib/types/shared";
  import SanityPicture from "$lib/SanityPicture.svelte";
  import Button from "$lib/Button.svelte";
  import { onDestroy } from "svelte";

  interface Props {
    images?: ImageWithMeta[];
    thumbnails?: ImageWithMeta[] | undefined;
    sizes?: string | undefined;
    class?: string;
  }

  const imageObserver: IntersectionObserver[] = [];
  const componentObserver: IntersectionObserver[] = [];

  const imageInViewAction = (node: HTMLElement, i: number) => {
    const imageObserver = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && scrollToImage === -1) {
          current = i;
        }
      },
      { root: document.querySelector(".horizontal-scroll"), threshold: 0.9 }
    );

    imageObserver.observe(node);
  };

  const componentInViewKeymappings = (node: HTMLElement) => {
    const componentObserver = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        keyControl = true;
      } else {
        keyControl = false;
      }
    });

    componentObserver.observe(node);
  };

  const disconnectObservers = () => {
    imageObserver.forEach((observer) => observer.disconnect());
    componentObserver.forEach((observer) => observer.disconnect());
  };

  const next = (_: Event, opt?: { focusThumbnail?: boolean }) => {
    scrollToImage = current === images.length - 1 ? 0 : current + 1;

    // focus thumbnail of index scrollToImage
    if (opt?.focusThumbnail) {
      const thumbnail =
        thumbnailsContainer?.children[0].children[scrollToImage].querySelector("button");
      if (thumbnail) thumbnail.focus();
    }
  };

  const prev = (_: Event, opt?: { focusThumbnail?: boolean }) => {
    scrollToImage = !current ? images.length - 1 : current - 1;

    if (opt?.focusThumbnail) {
      const thumbnail =
        thumbnailsContainer?.children[0].children[scrollToImage].querySelector("button");
      if (thumbnail) thumbnail.focus();
    }
  };

  let {
    images = [],
    thumbnails = undefined,
    sizes = undefined,
    class: classes = "",
  }: Props = $props();
  let keyControl = true;
  let thumbnailsContainer: HTMLElement | undefined = $state();
  let thumbnailsWidth = $state(Array(images.length).fill(0));
  let imagesContainer: HTMLElement | undefined = $state();
  let imagesContainerWidth = $state(0);
  let scrollThumbnailsTimeout: ReturnType<typeof setTimeout> | undefined;
  let scrollImagesTimeout: ReturnType<typeof setTimeout> | undefined;
  let current = $state(0);
  let scrollToImage = $state(-1);
  let updatingImages = $derived(!images);
  let imagesRefState = $state(() => images?.map((x) => x?.src || "").join("") || "");
  let imagesRef = $derived(() => images?.map((x) => x?.src || "").join("") || "");

  // When new images, reset current and scroll
  $effect(() => {
    if (imagesRef !== imagesRefState) {
      imagesRefState = imagesRef;
      current = 0;
      scrollToImage = -1;
    }
  });

  $effect(() => {
    if (imagesContainer && images?.length)
      imagesContainer.scrollTo({ left: 0, behavior: "instant" });
  });

  $effect(() => {
    if (thumbnailsContainer && images?.length)
      thumbnailsContainer.scrollTo({ left: 0, behavior: "instant" });
  });

  $effect(() => {
    if (thumbnailsContainer && current === images.length - 1) {
      thumbnailsContainer.scrollTo({ left: thumbnailsContainer.scrollWidth, behavior: "smooth" });
    } else if (thumbnailsContainer) {
      // try to scroll thumbnail container halfway into preceding thumbnail
      try {
        clearTimeout(scrollThumbnailsTimeout);
        scrollThumbnailsTimeout = setTimeout(() => {
          // `scrollPosition` = half width of directly preceding element + width of other preceding elements
          const scrollPosition = thumbnailsWidth
            .slice(0, current)
            .reduce((a, b, i) => a + (i === current ? 0 : i === current - 1 ? b / 2 : b), 0);
          thumbnailsContainer?.scrollTo({ left: scrollPosition, behavior: "smooth" });
        }, 250);
      } catch (error) {
        console.warn(error);
      }
    }
  });

  $effect(() => {
    if (scrollToImage > -1 && imagesContainer) {
      clearTimeout(scrollImagesTimeout);
      current = scrollToImage;
      const imageContainerScrollPosition = scrollToImage * imagesContainerWidth;
      imagesContainer.scrollTo({ left: imageContainerScrollPosition, behavior: "smooth" });
      scrollImagesTimeout = setTimeout(() => (scrollToImage = -1), 250);
    }
  });

  onDestroy(() => disconnectObservers());
</script>

<svelte:window
  onkeydown={(e) => {
    if (keyControl && (e.key === "ArrowLeft" || e.key === "h")) prev(e, { focusThumbnail: true });
    else if (keyControl && (e.key === "ArrowRight" || e.key === "l"))
      next(e, { focusThumbnail: true });
  }}
/>

<div
  class="transition-opacity {classes}"
  class:opacity-0={updatingImages}
  aria-label="Produktbilder"
  use:componentInViewKeymappings
>
  {#if images.length > 1}
    <div class="relative">
      <p class="absolute text-sm top-0 m-0 right-2 z-1 mix-blend-plus-lighter text-$fg-muted">
        <span class="sr-only">Bilde</span>
        <span class="inline-block">
          {current + 1}/{images.length}
        </span>
      </p>
      <div
        style="--_cols:{images.length}"
        class="mt-0 grid horizontal-scroll overflow-hidden"
        bind:this={imagesContainer}
        bind:clientWidth={imagesContainerWidth}
      >
        {#each images as image, i (image)}
          <p class="my-0" use:imageInViewAction={i}>
            <SanityPicture
              pictureObject={image}
              {sizes}
              loading={i === 0 ? "eager" : "lazy"}
              class="w-full aspect-video object-scale-down"
            />
          </p>
        {/each}
      </div>
      <Button
        arrow
        class="not-focus:border-transparent bg-transparent image-browse-button absolute left--2 laptop:left--8 top-[calc(50%-1.5rem)] min-w-[3rem] h-[3rem] p-0 z-1 rotate-180"
        onclick={prev}><span class="sr-only">Forrige</span></Button
      >
      <Button
        arrow
        class="not-focus:border-transparent bg-transparent image-browse-button absolute right--2 laptop:right--8 top-[calc(50%-1.5rem)] min-w-[3rem] h-[3rem] p-0 z-1"
        onclick={next}><span class="sr-only">Neste</span></Button
      >
    </div>
  {:else if images?.length}
    <p class="mt-0">
      <SanityPicture
        pictureObject={images[0]}
        {sizes}
        loading="eager"
        class="w-full aspect-video object-contain"
      />
    </p>
  {/if}

  <div class="relative">
    <p class="text-center m-0 sr-only"><small>Produktbilder</small></p>

    {#if images?.length > 1 && thumbnails?.length}
      <div class="touch-horizontal-scroll" bind:this={thumbnailsContainer}>
        <ul class="thumbnails my-1 px-0 list-none justify-center flex flex-wrap w-max mx-a">
          {#each thumbnails as image, i (image)}
            <li bind:clientWidth={thumbnailsWidth[i]}>
              <button
                class:current={current === i}
                class="button button-link rounded-0 min-w-[3rem] mx-1 px-0 py-2 mt-1"
                onclick={() => (scrollToImage = i)}
              >
                <SanityPicture
                  pictureObject={image}
                  sizes={`(min-width: ${br.desktop}) 15vw, (min-width: ${br.laptop}) 20vw, 40vw`}
                  loading="eager"
                  class="h-[3rem] object-contain"
                />
              </button>
            </li>
          {/each}
        </ul>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  div :global(.image-browse-button:focus) {
    color: var(--primary-strong);
  }

  .thumbnails {
    button {
      box-shadow: inset 0 1px var(--skodde);

      &.current,
      &.current:hover {
        box-shadow: inset 0 1px var(--primary-strong);
      }

      &:hover {
        box-shadow: inset 0 1px;
      }

      &:focus-visible {
        box-shadow: inset 0 3px var(--primary-strong);
      }
    }
  }

  /* Horizontal scroll specific styling */

  :global(:is(.touch-horizontal-scroll, .horizontal-scroll)) {
    overflow-x: auto;
    overflow-y: hidden;

    /* Hide scrollbar but keep functionality and a11y(?) */
    scrollbar-width: none; /* Firefox */

    /* Custom scrollbar for Chrome, Safari and Opera */
    /* width */
    &::-webkit-scrollbar {
      width: 0;
    }
  }

  .horizontal-scroll {
    grid-template-columns: repeat(var(--_cols, 1), 100%);
    scroll-snap-type: x mandatory;
  }

  .horizontal-scroll > :global(*) {
    scroll-snap-align: center;
  }
</style>
